/**
 * `@ae/crdt` — Y.Doc representation van de workspace-state.
 *
 * Doel: één gedeeld document per (workspace, branch) waar alle clients
 * tegen syncen via Yjs' update-protocol. Het Durable Object houdt de
 * canonical Y.Doc in memory, persisteert periodiek naar R2, en
 * broadcast updates over WebSocket naar alle verbonden clients.
 *
 * **Shape van het document:**
 *   ydoc.getMap("concepts")  → Y.Map<conceptId,  Y.Map<field, value>>
 *   ydoc.getMap("relations") → Y.Map<relationId, Y.Map<field, value>>
 *   ydoc.getMap("diagrams")  → Y.Map<diagramId,  Y.Map<field, value>>
 *
 * Inner-record Y.Map slaat scalaire velden op als primitief (string,
 * number, boolean, null) en complexe velden (BilingualText, attributes,
 * tags) als plain JSON-objecten. Dit verliest fine-grained merging binnen
 * die complexe velden — concurrent renames van dezelfde concept gaan via
 * last-write-wins per field-slot.
 *
 * **Uitzondering: viewState.** Die is sinds 2026-08-20 wél opgesplitst, in
 * drie lagen: `Y.Map<entryKey, Y.Map<veld, waarde>>` per diagram. Daar zat
 * namelijk het echte gegevensverlies — twee sessies die verschillende
 * objecten op één diagram aanraakten, overschreven elkaars plaatsing.
 * Bladeren binnen een entry blijven plain JSON: `position` hoort als geheel
 * te bewegen en een half samengevoegde `waypoints`-array levert een lijn op
 * die niemand heeft getekend. Zie `docs/architecture/viewstate-crdt.md`.
 *
 * Conflict-modes die wél werken:
 *   - User A renamet concept-X, user B verplaatst concept-X →
 *     name-edit en viewState-edit beide bewaard (verschillende slots).
 *   - User A maakt concept-Y aan, user B maakt concept-Z aan →
 *     beide bewaard (verschillende top-level keys).
 *   - User A delete't concept-X (tombstone), user B updatet 'm →
 *     winnaar bepaald door volgorde van updates; tombstone is een
 *     gewone field-update dus laatste schrijver wint.
 */
import type { Concept, Diagram, Relation } from "@ae/domain";
import * as Y from "yjs";

export const CONCEPTS_KEY = "concepts";
export const RELATIONS_KEY = "relations";
export const DIAGRAMS_KEY = "diagrams";

/**
 * Velden van een Concept zoals ze in de inner Y.Map staan. Type is
 * losse `unknown` omdat Yjs zelf geen TS-types kent — runtime check
 * doen we bij decode.
 */
type RecordValue = unknown;

// ── Encode (domain → Y.Doc) ───────────────────────────────────────────

/**
 * Schrijf een volledige snapshot van concepten/relaties/diagrammen in
 * een (eventueel lege) Y.Doc. Bestaande sleutels worden vervangen,
 * sleutels die niet in de snapshot zitten blijven staan — caller is
 * verantwoordelijk voor wipen indien gewenst.
 *
 * Wraps alle writes in één Y-transaction zodat clients één enkele
 * update-event ontvangen.
 */
export function applySnapshotToDoc(
  doc: Y.Doc,
  data: { concepts: Concept[]; relations: Relation[]; diagrams: Diagram[] },
  origin: unknown = "snapshot",
): void {
  doc.transact(() => {
    const concepts = doc.getMap<Y.Map<RecordValue>>(CONCEPTS_KEY);
    for (const c of data.concepts) {
      concepts.set(c.id, conceptToYMap(c));
    }

    const relations = doc.getMap<Y.Map<RecordValue>>(RELATIONS_KEY);
    for (const r of data.relations) {
      relations.set(r.id, relationToYMap(r));
    }

    const diagrams = doc.getMap<Y.Map<RecordValue>>(DIAGRAMS_KEY);
    for (const d of data.diagrams) {
      diagrams.set(d.id, diagramToYMap(d));
    }
  }, origin);
}

/** Volledige wipe + apply — voor restore-flows. */
export function replaceDocWithSnapshot(
  doc: Y.Doc,
  data: { concepts: Concept[]; relations: Relation[]; diagrams: Diagram[] },
  origin: unknown = "snapshot-replace",
): void {
  doc.transact(() => {
    doc.getMap(CONCEPTS_KEY).clear();
    doc.getMap(RELATIONS_KEY).clear();
    doc.getMap(DIAGRAMS_KEY).clear();
  }, origin);
  applySnapshotToDoc(doc, data, origin);
}

function conceptToYMap(c: Concept): Y.Map<RecordValue> {
  const m = new Y.Map<RecordValue>();
  m.set("id", c.id);
  m.set("typeId", c.typeId);
  m.set("extendsTypeId", c.extendsTypeId);
  m.set("name", { ...c.name });
  m.set("description", { ...(c.description ?? {}) });
  m.set("attributes", { ...(c.attributes ?? {}) });
  m.set("tags", [...(c.tags ?? [])]);
  m.set("ownerEmail", c.ownerEmail);
  m.set("ownerTeamId", c.ownerTeamId);
  m.set("parentId", c.parentId);
  m.set("version", c.version);
  m.set("experimentalInBranch", c.experimentalInBranch);
  m.set("tombstoneInBranch", c.tombstoneInBranch);
  return m;
}

function relationToYMap(r: Relation): Y.Map<RecordValue> {
  const m = new Y.Map<RecordValue>();
  m.set("id", r.id);
  m.set("typeId", r.typeId);
  m.set("sourceConceptId", r.sourceConceptId);
  m.set("targetConceptId", r.targetConceptId);
  m.set("label", { ...(r.label ?? {}) });
  m.set("attributes", { ...(r.attributes ?? {}) });
  m.set("ownerEmail", r.ownerEmail);
  m.set("ownerTeamId", r.ownerTeamId);
  m.set("version", r.version);
  m.set("experimentalInBranch", r.experimentalInBranch);
  m.set("tombstoneInBranch", r.tombstoneInBranch);
  return m;
}

function diagramToYMap(d: Diagram): Y.Map<RecordValue> {
  const m = new Y.Map<RecordValue>();
  m.set("id", d.id);
  m.set("name", { ...d.name });
  // Description is optional — alleen schrijven indien aanwezig.
  if (d.description) {
    m.set("description", { ...d.description });
  } else {
    m.set("description", null);
  }
  m.set("notationPack", d.notationPack);
  m.set("viewKind", d.viewKind);
  m.set("viewState", viewStateToYMap(d.viewState ?? {}));
  return m;
}

/**
 * ViewState als drie lagen in plaats van één slot.
 *
 * `Y.Map<entryKey, Y.Map<veld, waarde>>` — zo raakt een sleep van object A
 * niet aan de plaatsing van object B, en raakt "verbergen" van hetzelfde
 * object niet aan z'n positie. Precies de korrel waarop op 19 augustus
 * plaatsingen verdwenen toen twee sessies elkaars blob overschreven.
 *
 * Bladeren blijven plain JSON: `position` is `{x, y}` en hoort als geheel
 * te bewegen, en `waypoints` is een array waarvan een deel-merge een lijn
 * zou opleveren die niemand heeft getekend.
 */
function viewStateToYMap(viewState: Record<string, unknown>): Y.Map<RecordValue> {
  const outer = new Y.Map<RecordValue>();
  for (const [key, entry] of Object.entries(viewState)) {
    outer.set(key, entryToYMap(entry));
  }
  return outer;
}

function entryToYMap(entry: unknown): Y.Map<RecordValue> {
  const inner = new Y.Map<RecordValue>();
  if (entry && typeof entry === "object" && !Array.isArray(entry)) {
    for (const [field, value] of Object.entries(entry as Record<string, unknown>)) {
      inner.set(field, value === undefined ? null : JSON.parse(JSON.stringify(value)));
    }
  }
  return inner;
}

// ── Decode (Y.Doc → domain) ───────────────────────────────────────────

export interface DecodedDoc {
  concepts: Concept[];
  relations: Relation[];
  diagrams: Diagram[];
}

/**
 * Lees alle records uit het Y.Doc als plain domain-objecten. Geen
 * subscription — caller doet één snapshot-read. Voor live sync hoor
 * je `observeDeep` op de doc te gebruiken.
 */
export function decodeDoc(doc: Y.Doc): DecodedDoc {
  const concepts: Concept[] = [];
  const relations: Relation[] = [];
  const diagrams: Diagram[] = [];

  const cMap = doc.getMap<Y.Map<RecordValue>>(CONCEPTS_KEY);
  for (const y of cMap.values()) {
    const c = yMapToConcept(y);
    if (c) concepts.push(c);
  }

  const rMap = doc.getMap<Y.Map<RecordValue>>(RELATIONS_KEY);
  for (const y of rMap.values()) {
    const r = yMapToRelation(y);
    if (r) relations.push(r);
  }

  const dMap = doc.getMap<Y.Map<RecordValue>>(DIAGRAMS_KEY);
  for (const y of dMap.values()) {
    const d = yMapToDiagram(y);
    if (d) diagrams.push(d);
  }

  return { concepts, relations, diagrams };
}

function asString(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}
function asStringOrNull(v: unknown): string | null {
  return v === null || v === undefined ? null : typeof v === "string" ? v : null;
}
function asNumber(v: unknown, fallback = 1): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}
function asObject<T extends object>(v: unknown, fallback: T): T {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as T) : fallback;
}
function asArray<T>(v: unknown, fallback: T[]): T[] {
  return Array.isArray(v) ? (v as T[]) : fallback;
}

/**
 * Leest viewState terug, in beide vormen.
 *
 * Documenten die vóór de opsplitsing zijn gebootstrapt (of uit een oudere
 * R2-blob komen) dragen viewState nog als één plain object. Die moeten
 * leesbaar blijven, anders raakt een branch z'n plaatsingen kwijt op het
 * moment dat een oude blob wordt teruggehaald.
 */
function readViewState(raw: unknown): Record<string, unknown> {
  if (raw instanceof Y.Map) {
    const out: Record<string, unknown> = {};
    for (const [key, entry] of raw.entries()) {
      out[key] = entry instanceof Y.Map ? Object.fromEntries(entry.entries()) : entry;
    }
    return out;
  }
  return asObject(raw, {});
}

function yMapToConcept(m: Y.Map<RecordValue>): Concept | null {
  const id = asString(m.get("id"));
  const typeId = asString(m.get("typeId"));
  const ownerEmail = asString(m.get("ownerEmail"));
  if (!id || !typeId || !ownerEmail) return null;
  return {
    id,
    typeId,
    extendsTypeId: asStringOrNull(m.get("extendsTypeId")),
    name: asObject(m.get("name"), {}),
    description: asObject(m.get("description"), {}),
    attributes: asObject(m.get("attributes"), {}),
    tags: asArray<string>(m.get("tags"), []),
    ownerEmail,
    ownerTeamId: asStringOrNull(m.get("ownerTeamId")),
    parentId: asStringOrNull(m.get("parentId")),
    version: asNumber(m.get("version"), 1),
    experimentalInBranch: asStringOrNull(m.get("experimentalInBranch")),
    tombstoneInBranch: asStringOrNull(m.get("tombstoneInBranch")),
    // M5+: stereotype-bundles (zie packages/domain/src/types.ts).
    // Default lege array voor concepten die nog geen stereotype-veld
    // in Yjs hebben — backwards-compat met pre-M5-data.
    stereotypes: asArray<string>(m.get("stereotypes"), []),
    // M5-§3: alternative labels per view-resolver-id (sharp/c4/...)
    altLabels: asObject(m.get("altLabels"), {}) as Record<string, Record<string, string>>,
  };
}

function yMapToRelation(m: Y.Map<RecordValue>): Relation | null {
  const id = asString(m.get("id"));
  const typeId = asString(m.get("typeId"));
  const sourceConceptId = asString(m.get("sourceConceptId"));
  const targetConceptId = asString(m.get("targetConceptId"));
  const ownerEmail = asString(m.get("ownerEmail"));
  if (!id || !typeId || !sourceConceptId || !targetConceptId || !ownerEmail) return null;
  return {
    id,
    typeId,
    sourceConceptId,
    targetConceptId,
    label: asObject(m.get("label"), {}),
    attributes: asObject(m.get("attributes"), {}),
    ownerEmail,
    ownerTeamId: asStringOrNull(m.get("ownerTeamId")),
    version: asNumber(m.get("version"), 1),
    experimentalInBranch: asStringOrNull(m.get("experimentalInBranch")),
    tombstoneInBranch: asStringOrNull(m.get("tombstoneInBranch")),
    // M5+: stereotype-bundles op relaties (bv. «C4 Connection»).
    // Default lege array — backwards-compat met pre-M5-data.
    stereotypes: asArray<string>(m.get("stereotypes"), []),
    // M5-§3: alternative labels per view-resolver-id
    altLabels: asObject(m.get("altLabels"), {}) as Record<string, Record<string, string>>,
  };
}

function yMapToDiagram(m: Y.Map<RecordValue>): Diagram | null {
  const id = asString(m.get("id"));
  const notationPack = asString(m.get("notationPack"));
  const viewKind = asString(m.get("viewKind"));
  if (!id || !notationPack || !viewKind) return null;
  const description = m.get("description");
  return {
    id,
    name: asObject(m.get("name"), {}),
    ...(description && typeof description === "object" && !Array.isArray(description)
      ? { description: description as Record<string, string> }
      : {}),
    notationPack,
    viewKind,
    viewState: readViewState(m.get("viewState")) as Diagram["viewState"],
    attributes: asObject(m.get("attributes"), {}),
    stereotypes: asArray<string>(m.get("stereotypes"), []),
  };
}

// ── Edit-helpers (mutators die het Y.Doc bijwerken) ───────────────────

/** Zet (of overschrijf) één concept als volledig record. */
export function setConcept(doc: Y.Doc, c: Concept, origin: unknown = "user"): void {
  doc.transact(() => {
    const m = doc.getMap<Y.Map<RecordValue>>(CONCEPTS_KEY);
    m.set(c.id, conceptToYMap(c));
  }, origin);
}

export function setRelation(doc: Y.Doc, r: Relation, origin: unknown = "user"): void {
  doc.transact(() => {
    const m = doc.getMap<Y.Map<RecordValue>>(RELATIONS_KEY);
    m.set(r.id, relationToYMap(r));
  }, origin);
}

export function setDiagram(doc: Y.Doc, d: Diagram, origin: unknown = "user"): void {
  doc.transact(() => {
    const m = doc.getMap<Y.Map<RecordValue>>(DIAGRAMS_KEY);
    m.set(d.id, diagramToYMap(d));
  }, origin);
}

/**
 * Zet één viewState-entry op één diagram — de plaatsing van één object.
 *
 * Dit is het schrijfpad waar het hele CRDT-ontwerp om draait: twee sessies
 * die verschillende objecten aanraken schrijven in verschillende sleutels en
 * verliezen elkaars werk niet. Zie `docs/architecture/viewstate-crdt.md`.
 *
 * `merge` bepaalt wat er met velden gebeurt die je niet noemt: standaard
 * blijven ze staan (zodat een sleep niet het verbergen van een ander wist).
 * Met `merge: false` vervang je de entry in z'n geheel.
 */
export function setViewStateEntry(
  doc: Y.Doc,
  diagramId: string,
  entryKey: string,
  entry: Record<string, unknown>,
  opts: { merge?: boolean; origin?: unknown } = {},
): void {
  const { merge = true, origin = "user" } = opts;
  doc.transact(() => {
    const diagrams = doc.getMap<Y.Map<RecordValue>>(DIAGRAMS_KEY);
    const d = diagrams.get(diagramId);
    if (!d) return;
    let outer = d.get("viewState");
    if (!(outer instanceof Y.Map)) {
      // Nog de oude vorm (één plain object) — nu omzetten, met behoud van
      // wat erin stond.
      outer = viewStateToYMap(asObject(outer, {} as Record<string, unknown>));
      d.set("viewState", outer);
    }
    const existing = (outer as Y.Map<RecordValue>).get(entryKey);
    if (!merge || !(existing instanceof Y.Map)) {
      (outer as Y.Map<RecordValue>).set(entryKey, entryToYMap(entry));
      return;
    }
    for (const [field, value] of Object.entries(entry)) {
      existing.set(field, value === undefined ? null : JSON.parse(JSON.stringify(value)));
    }
  }, origin);
}

/**
 * Vervangt de complete viewState van één diagram door de meegegeven stand.
 *
 * Bedoeld voor het moment waarop D1 gezaghebbend is en het document níet:
 * na een bootstrap uit een R2-dump, en straks als antwoord op het
 * invalidatie-seintje van een bulk-operatie. Bewust een vervanging en geen
 * samenvoeging — dit zegt "vergeet wat je dacht te weten".
 */
export function replaceDiagramViewState(
  doc: Y.Doc,
  diagramId: string,
  viewState: Record<string, unknown>,
  origin: unknown = "viewstate-refresh",
): void {
  doc.transact(() => {
    const d = doc.getMap<Y.Map<RecordValue>>(DIAGRAMS_KEY).get(diagramId);
    if (!d) return;
    d.set("viewState", viewStateToYMap(viewState));
  }, origin);
}

/** Haal één viewState-entry weg. Weglaten is geen verwijderen — hier ook
 *  niet, net zomin als in de samenvoegende PATCH. */
export function removeViewStateEntry(
  doc: Y.Doc,
  diagramId: string,
  entryKey: string,
  origin: unknown = "user",
): void {
  doc.transact(() => {
    const d = doc.getMap<Y.Map<RecordValue>>(DIAGRAMS_KEY).get(diagramId);
    const outer = d?.get("viewState");
    if (outer instanceof Y.Map) outer.delete(entryKey);
  }, origin);
}

/** De viewState van één diagram als plain object — handig voor de
 *  doorschrijf naar D1, die dezelfde blob schrijft als vandaag. */
export function readDiagramViewState(doc: Y.Doc, diagramId: string): Record<string, unknown> {
  const d = doc.getMap<Y.Map<RecordValue>>(DIAGRAMS_KEY).get(diagramId);
  return d ? readViewState(d.get("viewState")) : {};
}

/** Verwijder een concept uit het document. Hard-remove — voor tombstone
 *  gebruik je `setConcept` met `tombstoneInBranch` gevuld. */
export function removeConceptFromDoc(
  doc: Y.Doc,
  conceptId: string,
  origin: unknown = "user",
): void {
  doc.transact(() => {
    doc.getMap(CONCEPTS_KEY).delete(conceptId);
  }, origin);
}

export function removeRelationFromDoc(
  doc: Y.Doc,
  relationId: string,
  origin: unknown = "user",
): void {
  doc.transact(() => {
    doc.getMap(RELATIONS_KEY).delete(relationId);
  }, origin);
}

export function removeDiagramFromDoc(
  doc: Y.Doc,
  diagramId: string,
  origin: unknown = "user",
): void {
  doc.transact(() => {
    doc.getMap(DIAGRAMS_KEY).delete(diagramId);
  }, origin);
}

// ── Wire-protocol helpers ─────────────────────────────────────────────

/** Encodeer de huidige doc-state als binary update voor over-the-wire
 *  versturen. Wordt door de client gebruikt om eigen edits te broadcasten,
 *  en door de server om de initiële sync naar nieuwe verbindingen te
 *  pushen. */
export function encodeDocUpdate(doc: Y.Doc, encodedTargetState?: Uint8Array): Uint8Array {
  return Y.encodeStateAsUpdate(doc, encodedTargetState);
}

/** Pas een binary update toe op een Y.Doc. Origin-tag voorkomt dat we
 *  onze eigen updates terug-loopen wanneer we observeren. */
export function applyDocUpdate(doc: Y.Doc, update: Uint8Array, origin: unknown = "remote"): void {
  Y.applyUpdate(doc, update, origin);
}

/** Encodeer de huidige state-vector — wordt verzonden bij initiële
 *  handshake zodat de andere kant alleen het diff hoeft te sturen. */
export function encodeStateVector(doc: Y.Doc): Uint8Array {
  return Y.encodeStateVector(doc);
}
